import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase, validatePassword, updateUserPassword } from '../lib/supabase';
import { Lock, AlertCircle, CheckCircle, Shield } from 'lucide-react';

const UpdatePassword = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: ''
  });
  const [passwordStrength, setPasswordStrength] = useState({
    length: false,
    uppercase: false,
    lowercase: false,
    number: false,
    special: false
  });

  useEffect(() => {
    // Verificar que el usuario llegó aquí desde el email de recuperación
    const checkSession = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        navigate('/login');
      }
    };

    checkSession();
  }, [navigate]);

  // Validar la fortaleza de la contraseña en tiempo real
  useEffect(() => {
    const password = formData.password;
    setPasswordStrength({
      length: password.length >= 8,
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      number: /\d/.test(password),
      special: /[!@#$%^&*(),.?":{}|<>]/.test(password)
    });
  }, [formData.password]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.id]: e.target.value
    });
    setError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (formData.password !== formData.confirmPassword) {
      setError('Las contraseñas no coinciden');
      return;
    }

    try {
      setError('');
      setLoading(true);

      const result = await updateUserPassword(formData.password);
      
      if (!result.success) {
        throw new Error(result.message);
      }

      setSuccess(true);
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (err: any) {
      console.error('Error al actualizar contraseña:', err);
      setError(err.message || 'Error al actualizar la contraseña');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-dark-card rounded-lg shadow-xl p-8 text-center">
          <div className="flex justify-center mb-6">
            <div className="bg-green-500/10 rounded-full p-3">
              <CheckCircle className="h-12 w-12 text-green-500" />
            </div>
          </div>

          <h2 className="text-2xl font-bold text-white mb-4">
            ¡Contraseña Actualizada!
          </h2>

          <p className="text-gray-300">
            Tu contraseña ha sido actualizada correctamente. Serás redirigido al inicio de sesión...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-4">
      <div className="max-w-md w-full bg-dark-card rounded-lg shadow-xl p-8">
        <h2 className="text-2xl font-bold text-white text-center mb-4">
          Actualizar Contraseña
        </h2>
        
        <p className="text-gray-300 text-center mb-8">
          Introduce tu nueva contraseña.
        </p>

        {error && (
          <div className="mb-6 p-3 bg-red-600/20 border border-red-600 rounded flex items-center text-red-100 text-sm">
            <AlertCircle className="h-5 w-5 mr-2 flex-shrink-0" />
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-300">
              Nueva Contraseña
            </label>
            <div className="mt-1 relative">
              <input
                type="password"
                id="password"
                value={formData.password}
                onChange={handleChange}
                className="input-field pl-10"
                required
                disabled={loading}
                minLength={8}
              />
              <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            </div>
          </div>

          <div>
            <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-300">
              Confirmar Nueva Contraseña
            </label>
            <div className="mt-1 relative">
              <input
                type="password"
                id="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                className="input-field pl-10"
                required
                disabled={loading}
                minLength={8}
              />
              <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            </div>
          </div>

          <div className="p-4 bg-dark-overlay rounded-lg">
            <h3 className="text-sm font-medium text-white mb-3 flex items-center">
              <Shield className="h-4 w-4 text-primary mr-2" />
              Requisitos de Contraseña
            </h3>
            <ul className="space-y-2 text-sm">
              <li className={`flex items-center ${passwordStrength.length ? 'text-green-500' : 'text-gray-400'}`}>
                <div className={`h-1.5 w-1.5 rounded-full mr-2 ${passwordStrength.length ? 'bg-green-500' : 'bg-gray-400'}`} />
                Mínimo 8 caracteres
              </li>
              <li className={`flex items-center ${passwordStrength.uppercase ? 'text-green-500' : 'text-gray-400'}`}>
                <div className={`h-1.5 w-1.5 rounded-full mr-2 ${passwordStrength.uppercase ? 'bg-green-500' : 'bg-gray-400'}`} />
                Al menos una mayúscula
              </li>
              <li className={`flex items-center ${passwordStrength.lowercase ? 'text-green-500' : 'text-gray-400'}`}>
                <div className={`h-1.5 w-1.5 rounded-full mr-2 ${passwordStrength.lowercase ? 'bg-green-500' : 'bg-gray-400'}`} />
                Al menos una minúscula
              </li>
              <li className={`flex items-center ${passwordStrength.number ? 'text-green-500' : 'text-gray-400'}`}>
                <div className={`h-1.5 w-1.5 rounded-full mr-2 ${passwordStrength.number ? 'bg-green-500' : 'bg-gray-400'}`} />
                Al menos un número
              </li>
              <li className={`flex items-center ${passwordStrength.special ? 'text-green-500' : 'text-gray-400'}`}>
                <div className={`h-1.5 w-1.5 rounded-full mr-2 ${passwordStrength.special ? 'bg-green-500' : 'bg-gray-400'}`} />
                Al menos un carácter especial
              </li>
            </ul>
          </div>

          <button
            type="submit"
            disabled={loading || !Object.values(passwordStrength).every(Boolean)}
            className="btn-primary w-full flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                Actualizando...
              </>
            ) : (
              'Actualizar Contraseña'
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

export default UpdatePassword;